/**
 * PasswordChangeTab Component
 *
 * Change user's password with current password verification.
 * Features: current password validation, new password with strength indicator, confirm password matching.
 */

import { useState } from 'react';
import { useForm } from '@mantine/form';
import { IconLock, IconDeviceFloppy } from '@tabler/icons-react';
import {
  Stack,
  Card,
  Group,
  Button,
  PasswordInput,
  Text,
  Alert,
} from '../../../components/ui';
import { toast } from '../../../components/ui';
import { useAuthService } from '@actschurch/ccms-sdk';
import { IconAlertCircle, IconCircleCheck } from '@tabler/icons-react';
import type { ProfileTabProps } from '../types';

export function PasswordChangeTab({ userId, user }: ProfileTabProps) {
  const authService = useAuthService();
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const form = useForm({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validate: {
      oldPassword: (value) => {
        if (!value || value.trim() === '') return 'Current password is required';
        return null;
      },
      newPassword: (value) => {
        if (!value || value.trim() === '') return 'New password is required';
        if (value.length < 8) return 'Password must be at least 8 characters';
        // Optional: Add more complexity rules
        // if (!/[A-Z]/.test(value)) return 'Password must contain at least one uppercase letter';
        // if (!/[a-z]/.test(value)) return 'Password must contain at least one lowercase letter';
        // if (!/[0-9]/.test(value)) return 'Password must contain at least one number';
        return null;
      },
      confirmPassword: (value, values) => {
        if (!value || value.trim() === '') return 'Please confirm your password';
        if (value !== values.newPassword) return 'Passwords do not match';
        return null;
      },
    },
  });

  /**
   * Handle password change
   */
  const handleChangePassword = async (values: typeof form.values) => {
    setSaving(true);
    setSuccess(false);

    try {
      await toast.promise(
        authService.changePassword({
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        }),
        {
          loading: 'Changing password...',
          success: 'Password changed successfully',
          error: (err: any) => err.message || 'Failed to change password. Please check your current password.',
        }
      );

      // Clear form on success
      form.reset();
      setSuccess(true);

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (error: any) {
      console.error('[PasswordChangeTab] Error changing password:', error);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Get password strength indicator
   */
  const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 25;
    if (/[A-Z]/.test(password)) strength += 12.5;
    if (/[a-z]/.test(password)) strength += 12.5;
    if (/[0-9]/.test(password)) strength += 12.5;
    if (/[^A-Za-z0-9]/.test(password)) strength += 12.5;

    let label = 'Weak';
    let color = 'red';
    if (strength >= 75) {
      label = 'Strong';
      color = 'green';
    } else if (strength >= 50) {
      label = 'Medium';
      color = 'yellow';
    }

    return { strength, label, color };
  };

  const passwordStrength = form.values.newPassword ? getPasswordStrength(form.values.newPassword) : null;

  return (
    <form onSubmit={form.onSubmit(handleChangePassword)}>
      <Stack gap="lg">
        {/* Success Alert */}
        {success && (
          <Alert
            color="green"
            icon={<IconCircleCheck size={16} />}
            title="Success"
          >
            Your password has been changed successfully. Make sure to remember your new password.
          </Alert>
        )}

        <Card withBorder>
          <Stack gap="md">
            <div>
              <Text size="lg" fw={600}>
                Change Password
              </Text>
              <Text size="sm" c="dimmed">
                Update your password to keep your account secure. Your new password must be at least 8 characters long.
              </Text>
            </div>

            <Alert
              color="blue"
              icon={<IconAlertCircle size={16} />}
              title="Password Requirements"
            >
              <Text size="sm">
                For security, you must provide your current password to change it. Choose a strong password with at least 8 characters.
              </Text>
            </Alert>

            {/* Current Password */}
            <PasswordInput
              label="Current Password"
              placeholder="Enter your current password"
              required
              {...form.getInputProps('oldPassword')}
              leftSection={<IconLock size={16} />}
            />

            {/* New Password */}
            <Stack gap="xs">
              <PasswordInput
                label="New Password"
                placeholder="Enter your new password"
                required
                {...form.getInputProps('newPassword')}
                leftSection={<IconLock size={16} />}
              />
              {passwordStrength && (
                <Group gap="xs">
                  <div
                    style={{
                      flex: 1,
                      height: 6,
                      backgroundColor: '#e9ecef',
                      borderRadius: 3,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${passwordStrength.strength}%`,
                        backgroundColor:
                          passwordStrength.color === 'green'
                            ? '#51cf66'
                            : passwordStrength.color === 'yellow'
                            ? '#ffd43b'
                            : '#ff6b6b',
                        transition: 'width 0.3s ease, background-color 0.3s ease',
                      }}
                    />
                  </div>
                  <Text size="xs" c={passwordStrength.color}>
                    {passwordStrength.label}
                  </Text>
                </Group>
              )}
            </Stack>

            {/* Confirm Password */}
            <PasswordInput
              label="Confirm New Password"
              placeholder="Confirm your new password"
              required
              {...form.getInputProps('confirmPassword')}
              leftSection={<IconLock size={16} />}
            />
          </Stack>
        </Card>

        {/* Action Button */}
        <Group justify="flex-end">
          <Button
            type="submit"
            leftSection={<IconDeviceFloppy size={16} />}
            loading={saving}
            gradientVariant="primary"
          >
            Change Password
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
